import { network } from '@utils'
import { handleReturn } from '@query/root'
import { PainStatus } from '@constants'

interface PaymentProps {
  slug: string
}

interface FetchPaymentProps extends PaymentProps {
  paymentId: string
}

export interface IPaymentStatus {
  created_at: string
  message_id: string
  status: PainStatus
}

export interface IPaymentData {
  id: string
  created_at: string
  statuses: IPaymentStatus[]
  payment_details: IPaymentDetails // Write only
}

export interface IPaymentDetails {
  holvi_account: number
  creditor: {
    name: string
    iban: string
    bic: string
  }
  amount: {
    amount: number
    currency: string
  }
  requested_execution_date: string
  reference?: string
  message?: string
}

export type PISPaymentStatusIntent = 'info' | 'success' | 'warning' | 'danger'

export interface IPISPaymentStatus {
  id: number
  status: string
  status_name: string | null
  status_description: string | null
  status_intent: PISPaymentStatusIntent | null
  created_at: string
}

export interface IPISPayment {
  id: string
  purchase_invoice: number | null
  payment_id: string
  statuses: IPISPaymentStatus[]
}

export const fetchPayment = async ({
  slug,
  paymentId
}: FetchPaymentProps): Promise<IPaymentData> => {
  const url = `/v1/payments/business/${slug}/pain/${paymentId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const createPayment = async (
  { slug }: PaymentProps,
  data: IPaymentDetails
): Promise<unknown> => {
  const url = `/v1/payments/business/${slug}/pain/`
  const { data: response, status } = await network.httpClient.request({
    url,
    method: 'POST',
    data: {
      payment_details: data
    },
    validateStatus: () => true
  })
  return handleReturn({ data: response, status })
}

interface PurchaseInvoicePaymentProps extends PaymentProps {
  purchaseInvoiceId: number
}

export interface IPISPaymentShortcut {
  id: number
  provider_code: string
  provider_name: string | null
  provider_logo_url: string | null
}

export interface IDeletePISPaymentShortcut {
  shortcutId: number
}

export interface IPISPurchaseInvoicePaymentPayload {
  shortcut_id?: number
  provider_code?: string
  redirect_url?: string
}

export interface IPISPurchaseInvoicePaymentResponse {
  payment_url: string
}

export const createPurchaseInvoicePayment = async (
  { slug, purchaseInvoiceId }: PurchaseInvoicePaymentProps,
  data: IPaymentDetails
) => {
  const url = `/v1/payments/business/${slug}/pay/purchase_invoice/${purchaseInvoiceId}/`
  const { data: response, status } = await network.httpClient.request({
    url,
    method: 'POST',
    data: {
      payment_details: data
    },
    validateStatus: () => true
  })
  return handleReturn({ data: response, status })
}

export const createPISPurchaseInvoicePayment = async (
  { slug, purchaseInvoiceId }: PurchaseInvoicePaymentProps,
  data: IPISPurchaseInvoicePaymentPayload
): Promise<IPISPurchaseInvoicePaymentResponse> => {
  const url = `/v1/payments/business/${slug}/pay/purchase_invoice/${purchaseInvoiceId}/pis/`
  const { data: response, status } = await network.httpClient.request({
    url,
    method: 'POST',
    data,
    validateStatus: () => true
  })
  return handleReturn({ data: response, status })
}

export const fetchPISPaymentShortcuts = async ({
  slug
}: PaymentProps): Promise<IPISPaymentShortcut[]> => {
  const url = `/v1/payments/business/${slug}/pis/shortcuts/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  if (Array.isArray(data)) {
    return data
  }
  if (data && Array.isArray(data.results)) {
    return data.results
  }
  return []
}

export interface IPISProvider {
  provider_code: string
  provider_name: string | null
  provider_logo_url: string | null
  country_code: string | null
}

interface FetchPISProvidersParams extends PaymentProps {
  country: string
  search?: string
  page?: number
  page_size?: number
}

interface IPISProviderListResponse {
  next: string | null
  results: IPISProvider[]
}

export const fetchPISProviders = async ({
  slug,
  country,
  search,
  page = 1,
  page_size = 24
}: FetchPISProvidersParams): Promise<IPISProviderListResponse> => {
  const url = `/v1/payments/business/${slug}/pis/providers/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'GET',
    params: {
      country,
      search,
      page,
      page_size
    }
  })

  return handleReturn({ status, data })
}

export const deletePISPaymentShortcut = async ({
  slug,
  shortcutId
}: PaymentProps & IDeletePISPaymentShortcut): Promise<void> => {
  const url = `/v1/payments/business/${slug}/pis/shortcuts/${shortcutId}/`
  await network.httpClient.request({
    url,
    method: 'DELETE'
  })
}
